import { Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subject, map, takeUntil } from 'rxjs';
import { ObjectConfigMappingService } from 'src/app/Common/ObjectConfigMapping/ObjectConfigMapping.service';
import { UserLoginService } from 'src/app/Common/UserLoginService/userlogin.service';
import { ExportDataService } from 'src/app/Common/ExportDataService/exportData.service';
import { ExportDataResponseDTO } from 'src/app/Common/ExportDataService/exportData.interface';
import { ValueListService } from 'src/app/Common/ValueListService/value-list.service';

/** VLCode grouping the tenant-configurable convert targets — vliCode must match ConvertOption.targetType, vliName is shown as-is */
const EXPORT_DATA_VL_CODE = 'ExportData';

interface ConvertTarget {
    targetType: string;   // SP @TargetType value — matched against ValueListItem.vliCode
    route: string;        // Angular route for post-convert navigation
}

interface ConvertOption extends ConvertTarget {
    label: string;        // dropdown display text — always ValueListItem.vliName
}

@Component({
    selector: 'app-export-data-button',
    templateUrl: './export-data-button.component.html',
    styleUrls: ['./export-data-button.component.css']
})
export class ExportDataButtonComponent implements OnInit, OnDestroy {

    // ── Inputs ────────────────────────────────────────────────────────────────
    @Input({ required: true }) SourceType: string = '';
    @Input({ required: true }) SourceMasterId: number = 0;
    /** Optional — shown read-only in convert form */
    @Input() SourceDocNo: string = '';
    /** Master record — used to show company name (CName) in the convert modal */
    @Input() SourceMasterData: any = null;
    /** Detail rows — shown as reference table in the convert modal */
    @Input() SourceDetailData: any[] = [];
    /**
     * Optional loader for list pages: called in openForRow() to fetch detail rows.
     * Provide an arrow function that returns an Observable of the detail response.
     * Example: (id) => this.detailService.ReadByMasterId({ masterId: id, ... })
     */
    @Input() detailLoader: ((masterId: number) => Observable<any>) | null = null;
    /**
     * true  → renders the "Convert" dropdown button (use in detail page headers)
     * false → button hidden; call openForRow() via @ViewChild from list pages
     */
    @Input() showDropdownButton: boolean = true;

    @Output() OnConverted = new EventEmitter<ExportDataResponseDTO>();

    // ── DI ───────────────────────────────────────────────────────────────────
    User           = inject(UserLoginService).User();
    private router       = inject(Router);
    private route        = inject(ActivatedRoute);
    private modalService = inject(NgbModal);
    private exportSvc    = inject(ExportDataService);
    private ocmService   = inject(ObjectConfigMappingService);
    private valueListSvc = inject(ValueListService);

    private destroy$ = new Subject<void>();

    // ── All 9 possible convert targets — label always comes from the ExportData Value List ──
    private readonly ALL_OPTIONS: ConvertTarget[] = [
        { targetType: 'Quotation',       route: '/crm/quotation'             },
        { targetType: 'SalesIndent',     route: '/sale/indent-master'        },
        { targetType: 'SalesOrder',      route: '/sale/sales-order'          },
        { targetType: 'SalesInvoice',    route: '/sale/sales'                },
        { targetType: 'SalesChallan',    route: '/sale/sales-challan'        },
        { targetType: 'PurIndent',       route: '/purchase/indent-master'    },
        { targetType: 'PurchaseOrder',   route: '/purchase/purchase-order'   },
        { targetType: 'PurchaseInvoice', route: '/purchase/purchase'         },
        { targetType: 'PurchaseChallan', route: '/purchase/purchase-challan' },
    ];

    // ── State ─────────────────────────────────────────────────────────────────
    isConvertEnabled  = false;
    visibleOptions: ConvertOption[] = [];

    selectedOption: ConvertOption | null = null;
    isSubmitting   = false;
    convertResult: ExportDataResponseDTO | null = null;

    /**
     * Cross-field rule: Valid Until must not precede the document date.
     * Only enforced when both are filled — blank Valid Until stays valid.
     */
    private static validUntilNotBeforeDate(group: AbstractControl): ValidationErrors | null {
        const docDate    = group.get('overrideDate')?.value;
        const validUntil = group.get('validUntil')?.value;
        if (!docDate || !validUntil) return null;
        return new Date(validUntil) < new Date(docDate)
            ? { validUntilBeforeDate: true }
            : null;
    }

    convertForm = new FormGroup({
        overrideDate:       new FormControl<string | null>(null),
        overrideRemarks:    new FormControl<string | null>(null),
        /** Quotation targets only — hidden for every other target */
        validUntil: new FormControl<string | null>(null),
    }, { validators: ExportDataButtonComponent.validUntilNotBeforeDate });

    @ViewChild('targetSelectModal') targetSelectModalTemplate!: ElementRef;
    @ViewChild('convertModal')      convertModalTemplate!: ElementRef;

    private targetSelectModalRef!: NgbModalRef;
    private convertModalRef!: NgbModalRef;

    // ── Lifecycle ─────────────────────────────────────────────────────────────
    ngOnInit(): void {
        this.loadObjectConfig(this.resolveMenuCode());
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ── Private helpers ───────────────────────────────────────────────────────
    private resolveMenuCode(): string {
        let r = this.route;
        while (r.firstChild) r = r.firstChild;

        const normalize = (path: string) =>
            path.split('/').find(seg => !!seg && !seg.startsWith(':')) ?? '';

        return normalize(r.routeConfig?.path ?? '')
            || normalize(r.parent?.routeConfig?.path ?? '');
    }

    private loadObjectConfig(menuCode: string): void {
        this.ocmService.ObjectConfigMapping_readByMenuCode({
            menuCode,
            tenantId:       this.User.companyId,
            tenantBranchId: this.User.loggedInBranchId
        }).subscribe({
            next: (res) => {
                const list = res.list ?? [];

                // OCM's 'conv' object gates whether the Convert button shows at all.
                // Which targets appear inside it — and their labels — come from the ExportData Value List.
                this.isConvertEnabled =
                    list.some(o => o.objectCode?.toLowerCase() === 'conv' && o.hasAccess === 1);

                if (this.isConvertEnabled) {
                    this.fetchVisibleOptions().subscribe(opts => this.visibleOptions = opts);
                }
            }
        });
    }

    private getOption(targetType: string): ConvertTarget | undefined {
        return this.ALL_OPTIONS.find(o => o.targetType === targetType);
    }

    /**
     * Tenant-configurable convert targets, sourced from ValueListItem rows under VLCode 'ExportData':
     * vliCode → matched to ConvertTarget.targetType, vliName → the dropdown label, shown as-is.
     * Current page's own SourceType is always excluded regardless of Value List config.
     */
    private fetchVisibleOptions(): Observable<ConvertOption[]> {
        return this.valueListSvc.getItemByVLCode(EXPORT_DATA_VL_CODE).pipe(
            takeUntil(this.destroy$),
            map(res => (res.valueListItems ?? [])
                .filter(i => i.isActive === 1 && i.vliCode !== this.SourceType)
                .sort((a, b) => (a.displaySeq || 0) - (b.displaySeq || 0))
                .reduce<ConvertOption[]>((acc, i) => {
                    const target = this.getOption(i.vliCode);
                    if (target) acc.push({ ...target, label: i.vliName });
                    return acc;
                }, []))
        );
    }

    // ── Public API — called via @ViewChild from list pages ────────────────────
    /**
     * Call from parent handleActionClick('Convert', rowData).
     * Updates source IDs then opens the target-picker modal.
     */
    openForRow(sourceMasterId: number, sourceDocNo: string = ''): void {
        this.SourceMasterId  = sourceMasterId;
        this.SourceDocNo     = sourceDocNo;
        this.SourceDetailData = [];

        this.fetchVisibleOptions().subscribe(opts => {
            this.visibleOptions = opts;

            if (!opts.length) return;

            // Fetch detail rows in background so they're ready when convert modal opens
            if (this.detailLoader) {
                this.detailLoader(sourceMasterId).subscribe({
                    next: (res) => {
                        this.SourceDetailData = Array.isArray(res) ? res : (res?.items ?? []);
                    }
                });
            }

            this.targetSelectModalRef = this.modalService.open(this.targetSelectModalTemplate, {
                size:     'sm',
                backdrop: true,
                keyboard: true
            });
        });
    }

    // ── Target picker → Convert form ──────────────────────────────────────────
    selectTarget(option: ConvertOption): void {
        this.targetSelectModalRef?.close();
        this.openConvertModal(option);
    }

    closeTargetSelector(): void {
        this.targetSelectModalRef?.close();
    }

    // ── Dynamic label derived from selected option ────────────────────────────
    get targetLabel(): string {
        return this.selectedOption?.label?.replace('Convert to ', '') ?? '';
    }

    /** "Valid Until" is a Quotation-only field — hidden for all other targets */
    get isQuotationTarget(): boolean {
        return this.selectedOption?.targetType === 'Quotation';
    }

    /** True once the user has touched the field and the date is out of order */
    get showValidUntilError(): boolean {
        return !!this.convertForm.errors?.['validUntilBeforeDate']
            && !!this.convertForm.get('validUntil')?.touched;
    }

    /** Try multiple field name variants — returns first non-empty value found */
    getVal(row: any, ...keys: string[]): any {
        for (const key of keys) {
            const v = row?.[key];
            if (v !== undefined && v !== null && v !== '') return v;
        }
        return '';
    }

    // ── Convert form modal ────────────────────────────────────────────────────
    openConvertModal(option: ConvertOption): void {
        this.selectedOption  = option;
        this.convertResult   = null;
        this.isSubmitting    = false;
        this.convertForm.reset();
        this.convertForm.patchValue({
        overrideDate: new Date().toISOString().split('T')[0],
        validUntil:   null,   
        });

        this.convertModalRef = this.modalService.open(this.convertModalTemplate, {
            size:     'xl',
            backdrop: 'static',
            keyboard: false
        });
    }

    closeModal(): void {
        this.convertModalRef?.close();
    }

    // ── Submit ────────────────────────────────────────────────────────────────
    onSubmit(): void {
        if (!this.selectedOption || !this.SourceMasterId) return;

        if (this.convertForm.invalid) {
            this.convertForm.markAllAsTouched();
            return;
        }

        this.isSubmitting = true;
        const { overrideDate, overrideRemarks, validUntil } = this.convertForm.value;

        this.exportSvc.ExportData_Convert({
            sourceType:      this.SourceType,
            sourceMasterId:  this.SourceMasterId,
            targetType:      this.selectedOption.targetType,
            tenantId:        +this.User.companyId,
            tenantBranchId:  this.User.loggedInBranchId,
            actionUser:      this.User.userId.toString(),
            overrideDate:    overrideDate    ?? null,
            overrideRemarks: overrideRemarks ?? null,
            validUntil:      validUntil      ?? null,
        }).subscribe({
            next: (res) => {
                this.isSubmitting  = false;
                this.convertResult = res;
                this.OnConverted.emit(res);
            },
            error: () => {
                this.isSubmitting = false;
            }
        });
    }

    // ── Navigate to created document ──────────────────────────────────────────
    navigateToTarget(): void {
        if (!this.convertResult || !this.selectedOption) return;
        this.closeModal();
        this.router.navigate([this.selectedOption.route]);
    }
}
